import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Link,
  CircularProgress,
  Chip,
  Fade,
  LinearProgress,
  alpha,
} from '@mui/material';
import {
  Public as PublicIcon,
  AutoAwesome as AIIcon,
  Verified as VerifiedIcon,
  Lock as LockIcon,
  Shield as ShieldIcon,
  Email as EmailIcon,
  CheckCircle as CheckCircleIcon,
  ArrowBack as ArrowBackIcon,
  Visibility,
  VisibilityOff,
} from '@mui/icons-material';
import { useNavigate, Link as RouterLink, useSearchParams } from 'react-router-dom';
import { useNotification } from '../../contexts/NotificationContext';
import { API_BASE_URL } from '../../constants';

// Same backgrounds as Login/Register for consistency
const BACKGROUNDS = [
  { url: 'https://images.unsplash.com/photo-1502602898657-3e91760cbb34?w=1920&q=95', location: 'Paris, France' },
  { url: 'https://images.unsplash.com/photo-1513635269975-59663e0ac1ad?w=1920&q=95', location: 'London, United Kingdom' },
  { url: 'https://images.unsplash.com/photo-1538332576228-eb5b4c4de6f5?w=1920&q=95', location: 'Dubai, UAE' },
  { url: 'https://images.unsplash.com/photo-1480714378408-67cf0d13bc1b?w=1920&q=95', location: 'New York, USA' },
  { url: 'https://images.unsplash.com/photo-1506973035872-a4ec16b8e8d9?w=1920&q=95', location: 'Sydney, Australia' },
  { url: 'https://images.unsplash.com/photo-1493976040374-85c8e12f0c0e?w=1920&q=95', location: 'Tokyo, Japan' },
  { url: 'https://images.unsplash.com/photo-1552832230-c0197dd311b5?w=1920&q=95', location: 'Rome, Italy' },
  { url: 'https://images.unsplash.com/photo-1525625293386-3f8f99389edd?w=1920&q=95', location: 'Singapore' },
];

const darkFieldSx = {
  '& .MuiOutlinedInput-root': {
    bgcolor: 'rgba(255,255,255,0.06)', borderRadius: 2, color: 'white',
    '& fieldset': { borderColor: 'rgba(255,255,255,0.12)' },
    '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.25)' },
    '&.Mui-focused fieldset': { borderColor: '#0EA5E9' },
  },
  '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.5)' },
  '& .MuiInputLabel-root.Mui-focused': { color: '#0EA5E9' },
  '& .MuiInputBase-input': { color: 'white' },
  '& .MuiInputBase-input::placeholder': { color: 'rgba(255,255,255,0.4)' },
  '& .MuiFormHelperText-root': { color: '#FCA5A5' },
};

type Step = 'email' | 'sent' | 'reset' | 'done';

const ForgotPasswordPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { showError, showSuccess } = useNotification();

  const [currentBg, setCurrentBg] = useState(() => Math.floor(Math.random() * BACKGROUNDS.length));
  const [nextBg, setNextBg] = useState<number | null>(null);
  const [fading, setFading] = useState(false);
  const [loading, setLoading] = useState(false);

  const [step, setStep] = useState<Step>('email');
  const [email, setEmail] = useState('');
  const [token, setToken] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Check for reset token in URL
  useEffect(() => {
    const urlToken = searchParams.get('token');
    if (urlToken) {
      setToken(urlToken);
      setStep('reset');
    }
  }, [searchParams]);

  // Preload & rotate backgrounds
  useEffect(() => {
    BACKGROUNDS.forEach(bg => { const img = new Image(); img.src = bg.url; });
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setNextBg((currentBg + 1) % BACKGROUNDS.length);
      setFading(true);
      setTimeout(() => {
        setCurrentBg(prev => (prev + 1) % BACKGROUNDS.length);
        setFading(false);
        setNextBg(null);
      }, 1000);
    }, 8000);
    return () => clearInterval(interval);
  }, [currentBg]);

  const bg = BACKGROUNDS[currentBg];

  const handleRequestReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});

    if (!email.trim()) {
      setErrors({ email: 'Email is required' });
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setErrors({ email: 'Please enter a valid email' });
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      const data = await res.json();

      if (res.ok || data.success) {
        setStep('sent');
        showSuccess('Reset instructions sent to your email');
      } else {
        setErrors({ email: data.message || 'Failed to send reset email' });
      }
    } catch {
      // Still show sent step (don't reveal if email exists)
      setStep('sent');
      showSuccess('If an account exists, reset instructions have been sent');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    const errs: Record<string, string> = {};

    if (!password) errs.password = 'Password is required';
    else if (password.length < 8) errs.password = 'Must be at least 8 characters';
    if (!confirmPassword) errs.confirmPassword = 'Please confirm your password';
    else if (password !== confirmPassword) errs.confirmPassword = 'Passwords do not match';

    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password }),
      });
      const data = await res.json();

      if (res.ok || data.success) {
        setStep('done');
        showSuccess('Password reset successfully!');
      } else {
        showError(data.message || 'Reset failed. Token may be expired.');
      }
    } catch {
      showError('Failed to reset password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Password strength
  const getStrength = (pw: string) => {
    if (!pw) return { score: 0, label: '', color: '' };
    let s = 0;
    if (pw.length >= 8) s++;
    if (pw.length >= 12) s++;
    if (/[A-Z]/.test(pw)) s++;
    if (/[0-9]/.test(pw)) s++;
    if (/[^A-Za-z0-9]/.test(pw)) s++;
    if (s <= 1) return { score: 20, label: 'Weak', color: '#EF4444' };
    if (s === 2) return { score: 40, label: 'Fair', color: '#F59E0B' };
    if (s === 3) return { score: 60, label: 'Good', color: '#F59E0B' };
    if (s === 4) return { score: 80, label: 'Strong', color: '#10B981' };
    return { score: 100, label: 'Very Strong', color: '#10B981' };
  };

  const pwStrength = getStrength(password);

  const renderContent = () => {
    switch (step) {
      case 'email':
        return (
          <Box component="form" onSubmit={handleRequestReset}>
            <Box sx={{ textAlign: 'center', mb: 2 }}>
              <Box sx={{ width: 56, height: 56, borderRadius: '50%', bgcolor: alpha('#0EA5E9', 0.15), display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 1.5 }}>
                <LockIcon sx={{ fontSize: 28, color: '#0EA5E9' }} />
              </Box>
              <Typography variant="h5" sx={{ fontWeight: 700, color: 'white', mb: 0.5 }}>
                Forgot Password?
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.8rem' }}>
                Enter your email and we'll send you reset instructions
              </Typography>
            </Box>

            <TextField fullWidth placeholder="Email address" type="email" size="small"
              value={email} onChange={(e) => { setEmail(e.target.value); setErrors({}); }}
              error={!!errors.email} helperText={errors.email}
              autoComplete="email" autoFocus sx={{ ...darkFieldSx, mb: 2 }}
            />

            <Button type="submit" fullWidth variant="contained" disabled={loading}
              sx={{
                py: 1.2, borderRadius: 2, fontWeight: 600, fontSize: '0.9rem', textTransform: 'none',
                background: 'linear-gradient(135deg, #0EA5E9 0%, #14B8A6 100%)',
                boxShadow: '0 4px 20px rgba(14, 165, 233, 0.3)',
                '&:hover': { background: 'linear-gradient(135deg, #0284C7 0%, #0D9488 100%)' },
                '&:disabled': { background: '#CBD5E1' },
              }}>
              {loading ? <CircularProgress size={24} color="inherit" /> : 'Send Reset Link'}
            </Button>
          </Box>
        );

      case 'sent':
        return (
          <Box sx={{ textAlign: 'center' }}>
            <Box sx={{ width: 64, height: 64, borderRadius: '50%', bgcolor: alpha('#10B981', 0.15), display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 2 }}>
              <EmailIcon sx={{ fontSize: 32, color: '#10B981' }} />
            </Box>
            <Typography variant="h5" sx={{ fontWeight: 700, color: 'white', mb: 1 }}>
              Check Your Email
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)', mb: 3, px: 2 }}>
              We've sent password reset instructions to <strong style={{ color: '#0EA5E9' }}>{email}</strong>. Check your inbox and spam folder.
            </Typography>

            <TextField fullWidth placeholder="Enter reset code (if using code)" size="small"
              value={token} onChange={(e) => setToken(e.target.value)}
              sx={{ ...darkFieldSx, mb: 2 }}
            />

            <Button fullWidth variant="contained" disabled={!token}
              onClick={() => setStep('reset')}
              sx={{
                py: 1.2, borderRadius: 2, fontWeight: 600, textTransform: 'none',
                background: 'linear-gradient(135deg, #0EA5E9 0%, #14B8A6 100%)',
                '&:hover': { background: 'linear-gradient(135deg, #0284C7 0%, #0D9488 100%)' },
                '&:disabled': { background: 'rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.3)' },
                mb: 1.5,
              }}>
              Continue with Code
            </Button>

            <Button fullWidth variant="text" onClick={() => { setStep('email'); setErrors({}); }}
              sx={{ color: 'rgba(255,255,255,0.6)', textTransform: 'none', fontSize: '0.85rem' }}>
              Didn't receive it? Resend
            </Button>
          </Box>
        );

      case 'reset':
        return (
          <Box component="form" onSubmit={handleResetPassword}>
            <Box sx={{ textAlign: 'center', mb: 2 }}>
              <Box sx={{ width: 56, height: 56, borderRadius: '50%', bgcolor: alpha('#0EA5E9', 0.15), display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 1.5 }}>
                <ShieldIcon sx={{ fontSize: 28, color: '#0EA5E9' }} />
              </Box>
              <Typography variant="h5" sx={{ fontWeight: 700, color: 'white', mb: 0.5 }}>
                Set New Password
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.8rem' }}>
                Choose a strong password for your account
              </Typography>
            </Box>

            <TextField fullWidth placeholder="New Password" size="small"
              type={showPassword ? 'text' : 'password'}
              value={password} onChange={(e) => { setPassword(e.target.value); setErrors({}); }}
              error={!!errors.password} helperText={errors.password}
              autoComplete="new-password"
              InputProps={{
                endAdornment: (
                  <Box onClick={() => setShowPassword(!showPassword)}
                    sx={{ cursor: 'pointer', display: 'flex', color: 'rgba(255,255,255,0.4)' }}>
                    {showPassword ? <VisibilityOff sx={{ fontSize: 20 }} /> : <Visibility sx={{ fontSize: 20 }} />}
                  </Box>
                ),
              }}
              sx={{ ...darkFieldSx, mb: 0.5 }}
            />

            {password && (
              <Box sx={{ mb: 1.5 }}>
                <LinearProgress variant="determinate" value={pwStrength.score}
                  sx={{ height: 3, borderRadius: 2, bgcolor: 'rgba(255,255,255,0.1)', '& .MuiLinearProgress-bar': { bgcolor: pwStrength.color, borderRadius: 2 } }}
                />
                <Typography variant="caption" sx={{ color: pwStrength.color, fontSize: '0.7rem', mt: 0.3, display: 'block' }}>
                  {pwStrength.label}
                </Typography>
              </Box>
            )}

            <TextField fullWidth placeholder="Confirm New Password" size="small"
              type="password" value={confirmPassword}
              onChange={(e) => { setConfirmPassword(e.target.value); setErrors({}); }}
              error={!!errors.confirmPassword} helperText={errors.confirmPassword}
              autoComplete="new-password"
              sx={{ ...darkFieldSx, mb: 2 }}
            />

            <Button type="submit" fullWidth variant="contained" disabled={loading}
              sx={{
                py: 1.2, borderRadius: 2, fontWeight: 600, fontSize: '0.9rem', textTransform: 'none',
                background: 'linear-gradient(135deg, #0EA5E9 0%, #14B8A6 100%)',
                boxShadow: '0 4px 20px rgba(14, 165, 233, 0.3)',
                '&:hover': { background: 'linear-gradient(135deg, #0284C7 0%, #0D9488 100%)' },
                '&:disabled': { background: '#CBD5E1' },
              }}>
              {loading ? <CircularProgress size={24} color="inherit" /> : 'Reset Password'}
            </Button>
          </Box>
        );

      case 'done':
        return (
          <Box sx={{ textAlign: 'center' }}>
            <Box sx={{ width: 64, height: 64, borderRadius: '50%', bgcolor: alpha('#10B981', 0.15), display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 2 }}>
              <CheckCircleIcon sx={{ fontSize: 36, color: '#10B981' }} />
            </Box>
            <Typography variant="h5" sx={{ fontWeight: 700, color: 'white', mb: 1 }}>
              Password Reset!
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)', mb: 3 }}>
              Your password has been successfully reset. You can now sign in with your new password.
            </Typography>
            <Button fullWidth variant="contained" onClick={() => navigate('/login')}
              sx={{
                py: 1.2, borderRadius: 2, fontWeight: 600, textTransform: 'none',
                background: 'linear-gradient(135deg, #0EA5E9 0%, #14B8A6 100%)',
                '&:hover': { background: 'linear-gradient(135deg, #0284C7 0%, #0D9488 100%)' },
              }}>
              Sign In
            </Button>
          </Box>
        );
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', position: 'relative', overflow: 'hidden', bgcolor: '#0F172A' }}>
      {/* Rotating Background */}
      <Box sx={{ position: 'absolute', inset: 0, zIndex: 0 }}>
        <Box sx={{
          position: 'absolute', inset: 0,
          backgroundImage: `url(${bg.url})`, backgroundSize: 'cover', backgroundPosition: 'center',
          transition: 'opacity 1s ease-in-out', opacity: fading ? 0 : 1,
          '&::after': { content: '""', position: 'absolute', inset: 0, background: 'linear-gradient(135deg, rgba(15,23,42,0.75) 0%, rgba(15,23,42,0.45) 50%, rgba(15,23,42,0.65) 100%)' },
        }} />
        {nextBg !== null && (
          <Box sx={{
            position: 'absolute', inset: 0,
            backgroundImage: `url(${BACKGROUNDS[nextBg].url})`, backgroundSize: 'cover', backgroundPosition: 'center',
            transition: 'opacity 1s ease-in-out', opacity: fading ? 1 : 0,
            '&::after': { content: '""', position: 'absolute', inset: 0, background: 'linear-gradient(135deg, rgba(15,23,42,0.75) 0%, rgba(15,23,42,0.45) 50%, rgba(15,23,42,0.65) 100%)' },
          }} />
        )}
      </Box>

      {/* Top Bar */}
      <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, zIndex: 10, display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: { xs: 2, md: 3 } }}>
        <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 1.5 }}>
          {[
            { icon: <PublicIcon sx={{ fontSize: 16 }} />, text: '249 countries' },
            { icon: <AIIcon sx={{ fontSize: 16 }} />, text: 'AI-Powered' },
            { icon: <VerifiedIcon sx={{ fontSize: 16 }} />, text: 'Premium' },
          ].map((item, i) => (
            <Chip key={i} icon={item.icon} label={item.text} size="small"
              sx={{ bgcolor: alpha('#FFFFFF', 0.15), backdropFilter: 'blur(10px)', color: 'white', fontWeight: 500, fontSize: '0.75rem', '& .MuiChip-icon': { color: 'white' }, border: `1px solid ${alpha('#FFFFFF', 0.2)}` }}
            />
          ))}
        </Box>
      </Box>

      {/* Card */}
      <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1, position: 'relative', p: { xs: 2, md: 4 } }}>
        <Fade in timeout={500}>
          <Box sx={{
            width: '100%', maxWidth: 400,
            background: 'linear-gradient(145deg, #1a1f3c 0%, #0d1025 50%, #141832 100%)',
            backdropFilter: 'blur(20px)', borderRadius: 3, p: { xs: 2.5, sm: 3 },
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
            border: '1px solid rgba(255, 255, 255, 0.08)',
          }}>
            {renderContent()}

            {/* Back to Login */}
            {step !== 'done' && (
              <Typography variant="caption" textAlign="center" display="block" sx={{ mt: 2, color: 'rgba(255, 255, 255, 0.7)' }}>
                <Link component={RouterLink} to="/login" sx={{ color: '#0EA5E9', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                  <ArrowBackIcon sx={{ fontSize: 14 }} /> Back to Sign In
                </Link>
              </Typography>
            )}

            {/* Security Badges */}
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1.5, pt: 1.5, mt: 2, borderTop: '1px solid rgba(255, 255, 255, 0.15)' }}>
              {[
                { icon: <LockIcon sx={{ fontSize: 14 }} />, text: 'HTTPS' },
                { icon: <ShieldIcon sx={{ fontSize: 14 }} />, text: 'Protected Reset' },
                { icon: <VerifiedIcon sx={{ fontSize: 14 }} />, text: 'Time-Limited Link' },
              ].map((badge, i) => (
                <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Box sx={{ color: '#10B981' }}>{badge.icon}</Box>
                  <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }} fontWeight={500}>{badge.text}</Typography>
                </Box>
              ))}
            </Box>

            <Typography variant="caption" display="block" textAlign="center" sx={{ mt: 1, fontSize: '0.65rem', color: 'rgba(255, 255, 255, 0.5)' }}>
              &copy; 2026 Tripnovac by Alpha Libra LLC
            </Typography>
          </Box>
        </Fade>
      </Box>

      {/* Bottom Right */}
      <Box sx={{ position: 'absolute', bottom: 20, right: 20, zIndex: 10, textAlign: 'right', display: { xs: 'none', md: 'block' } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
          <PublicIcon sx={{ fontSize: 16, color: '#0EA5E9' }} />
          <Typography variant="body2" sx={{ color: '#0EA5E9', fontWeight: 500 }}>{bg.location}</Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default ForgotPasswordPage;
